/**
 * This class only contains static API calls to the server
 */

import PoiData, { PoiExtraData } from './PoiData'
import I18N from '../utils/I18nMessageProvider'
import { POI_URL, SEARCH_URL } from '../constants/Constants'

class PoiRequests {

  /**
   * Requests extra data (description, photoUrl urls) from server for a given poi location id and language.
   * @param poi {Poi}
   * @param lang {string}
   * @returns {Promise<Poi, string>} In case of success the same poi object with its extra data will be returned
   */
  static extraData (poi, lang) {

    return new Promise((resolve, reject) => {
      if (!poi) {
        console.log(`No poi given. Instead got ${JSON.stringify(poi)}`)
        reject(I18N.get('tripproxy.errors.ta_generic_error'))
        return
      }

      if (poi.extraData) {
        resolve(poi)
        return
      }

      fetch(`/server/poi/location?location=${poi.id}&lang=${lang.substring(0, 2)}`).then(
        (response) => {
          if (response.status === 200) {
            response.json().then(
              (json) => {
                if (json && json.success && json.data) {
                  const extraData = new PoiExtraData(json.data)
                  extraData.applyToPoi(poi)
                  resolve(poi)
                }
                else if (json.error) {
                  reject(JSON.stringify(json.error))
                }
                else {
                  console.log('Couldn\'t get response from server when requesting additional data')
                  reject(I18N.get('tripproxy.errors.ta_error_additional_data'))
                }
              },
              (error) => {
                reject(error)
              }
            )
          }
        },
        (error) => {
          console.log(error)
          reject(error)
        }
      )
    })
  }

  /**
   * Indirectly calls the map api from trip advisor (through our server).
   * @param {{lat: number, lng: number}} coords The center of the area to search for.
   * @param lang {string}
   * @param categories
   * @param distanceKm The distance in km around coords to look for POIs.
   * @returns {Promise<PoiData, string>}
   */
  static mapCoords (coords, lang, categories, distanceKm = 10, countries = null) {
    return new Promise((resolve, reject) => {

      if (!coords) {
        console.log(`No coordinates given. Instead got ${JSON.stringify(coords)}`)
        reject(I18N.get('tripproxy.errors.ta_generic_error'))
        return
      }

      if (!distanceKm) {
        console.log('given distance was null')
      }
      const distanceKmToRequest = distanceKm || 10

      let url = `${POI_URL}?lat=${coords.lat % 90}&long=${coords.lng % 180}&lang=${lang.substring(0, 2)}&distance=${distanceKmToRequest}&lunit=km&categories=${categories.join(',')}`

      if (countries && countries.length > 0) {
        url += `&countries=${countries.join(',')}`
      }

      fetch(url).then(
        (response) => {
          if (response.status === 200) {
            response.json().then(
              (json) => {
                if (json.success) {
                  resolve(new PoiData(json))
                }
                else {
                  if (json.error.code === 151) {
                    reject({userMessageId: 'tripproxy.errors.ta_not_available'})
                  }
                  else {
                    reject()
                  }
                }
              },
              (error) => {
                reject(error)
              }
            )
          }
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  /**
   *
   * @param query {string}≠
   * @param position {{lat: number, lng: number}}
   * @param maxResultCount {number} Highest number of results the server may respond with
   * @returns {Promise<object, string>}
   */
  static searchQuery (query, position, maxResultCount = 20) {
    return new Promise((resolve, reject) => {
      if (query) {
        let url = `${SEARCH_URL}?q=${query}&lang=de&max=${maxResultCount}`
        if (position && position.lat && position.lng) {
          url += `&lat=${position.lat % 90}&long=${position.lng % 180}`
        }

        fetch(url).then(
          (response) => {
            if (response.status === 200) {
              response.json().then(
                (json) => {
                  resolve(json)
                },
                (error) => {
                  reject(error)
                }
              )
            }
          },
          (error) => {
            reject(error)
          }
        )
      }
    })
  }

}

export default PoiRequests



// WEBPACK FOOTER //
// ./src/data/PoiRequests.js